import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import {
  getHeatmapData,
  getSearchVolume,
  getPopularKeywords,
  getTrafficSources,
  getMostClickedRestaurants,
  getUniqueVisitors,
  getTotalUniqueVisitors,
  getEventPerformance,
  getEngagementFunnel,
  getCitySummary,
} from "../../../util/analyticsApi";
import GeographicHeatmap from "./analytics/GeographicHeatmap";
import MetricsCards from "./analytics/MetricsCards";
import SearchVolumeChart from "./analytics/SearchVolumeChart";
import PopularKeywordsChart from "./analytics/PopularKeywordsChart";
import TrafficSourcesChart from "./analytics/TrafficSourcesChart";
import MostClickedRestaurants from "./analytics/MostClickedRestaurants";
import EventPerformance from "./analytics/EventPerformance";
import EngagementFunnel from "./analytics/EngagementFunnel";
import ZeroResultQueries from "./analytics/ZeroResultQueries";
import CuisineDemandChart from "./analytics/CuisineDemandChart";
import LandingPagePieChart from "./analytics/LandingPagePieChart";
import LandingPageFunnel from "./analytics/LandingPageFunnel";
import SessionFlowTable from "./analytics/SessionFlowSankey";
import UniqueVisitorsTrend from "./analytics/UniqueVisitorsTrend";
import AdHocAnalyticsSection from "./analytics/AdHocAnalyticsSection";
import { exportToCsv } from "../../../util/csvExport";

const DashboardContainer = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  padding: 15px;
  background: #f5f5f5;
  border-radius: 8px;
  flex-wrap: wrap;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterLabel = styled.label`
  font-weight: bold;
  margin-right: 5px;
`;

const FilterInput = styled.input`
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Section = styled.div`
  margin-bottom: 30px;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const SectionTitle = styled.h2`
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
  border-bottom: 2px solid #4caf50;
  padding-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    font-size: 1.2em;
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const ExportButton = styled.button`
  padding: 4px 10px;
  font-size: 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  color: #555;
  margin-left: auto;

  &:hover {
    background: #f5f5f5;
  }
`;

const TabBar = styled.div`
  display: flex;
  gap: 2px;
  margin-bottom: 20px;
  border-bottom: 2px solid #e0e0e0;
`;

const TabButton = styled.button`
  padding: 10px 20px;
  border: none;
  background: ${(props) => (props.active ? "#4caf50" : "transparent")};
  color: ${(props) => (props.active ? "white" : "#333")};
  cursor: pointer;
  font-size: 14px;
  font-weight: ${(props) => (props.active ? "600" : "400")};
  border-radius: 6px 6px 0 0;
  margin-bottom: -2px;
  border-bottom: 2px solid ${(props) => (props.active ? "#4caf50" : "transparent")};

  &:hover {
    background: ${(props) => (props.active ? "#43a047" : "#f5f5f5")};
  }
`;

const PresetBar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;

const PresetButton = styled.button`
  padding: 6px 14px;
  border: 1px solid ${(props) => (props.active ? "#4caf50" : "#ddd")};
  border-radius: 4px;
  background: ${(props) => (props.active ? "#4caf50" : "white")};
  color: ${(props) => (props.active ? "white" : "#333")};
  cursor: pointer;
  font-size: 13px;
  font-weight: ${(props) => (props.active ? "600" : "400")};

  &:hover {
    background: ${(props) => (props.active ? "#43a047" : "#f5f5f5")};
  }
`;

const CitySelect = styled.select`
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 180px;

  @media (max-width: 768px) {
    min-width: unset;
    width: 100%;
  }
`;

const FilterChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #e8f5e9;
  border-radius: 16px;
  font-size: 13px;
  color: #2e7d32;

  button {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 14px;
    color: #2e7d32;
    padding: 0;
    line-height: 1;
  }
`;

const AnalyticsDashboard = ({ token, groups }) => {
  const [activeTab, setActiveTab] = useState("site");
  const [timePreset, setTimePreset] = useState("30d");
  const [showCustomDates, setShowCustomDates] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [cityData, setCityData] = useState(null);
  const [keywordFilter, setKeywordFilter] = useState(null);
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
    eventId: "",
    eventSlug: "",
    restaurantId: null,
  });

  const [cityList, setCityList] = useState([]);

  useEffect(function fetchCityListOnMount() {
    const loadCities = async () => {
      try {
        const res = await fetch("/api/cities");
        if (res.ok) {
          const cities = await res.json();
          setCityList(cities);
        }
      } catch (err) {
        console.error("Failed to load cities:", err);
      }
    };
    loadCities();
  }, []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    heatmap: [],
    searchVolume: [],
    keywords: [],
    trafficSources: [],
    restaurants: [],
    uniqueVisitors: [],
    eventPerformance: [],
    engagementFunnel: null,
  });

  const [summary, setSummary] = useState({
    totalSearches: 0,
    totalClicks: 0,
    uniqueVisitors: 0,
    activeEvents: 0,
  });

  useEffect(function loadDataWhenFiltersChange() {
    if (activeTab !== "site") return;
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedCity, keywordFilter, activeTab]);

  // Load city-specific data when city is selected
  useEffect(function loadCityDataWhenCityChanges() {
    if (!selectedCity) {
      setCityData(null);
      return;
    }
    const loadCityData = async () => {
      try {
        const result = await getCitySummary({
          cityLat: selectedCity.lat,
          cityLng: selectedCity.lng,
          cityRadius: selectedCity.defaultRadius,
          startDate: filters.startDate,
          endDate: filters.endDate,
        });
        setCityData(result);
      } catch (err) {
        console.error("Error loading city data:", err);
      }
    };
    loadCityData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity, filters.startDate, filters.endDate]);

  const applyTimePreset = (preset) => {
    setTimePreset(preset);
    if (preset === "custom") {
      setShowCustomDates(true);
      return;
    }
    setShowCustomDates(false);
    const now = new Date();
    const end = now.toISOString().split("T")[0];
    let daysBack;
    switch (preset) {
      case "7d": daysBack = 7; break;
      case "30d": daysBack = 30; break;
      case "90d": daysBack = 90; break;
      case "1y": daysBack = 365; break;
      default: daysBack = 30;
    }
    const start = new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0];
    setFilters((prev) => ({ ...prev, startDate: start, endDate: end }));
  };

  const handleCityChange = (e) => {
    const cityName = e.target.value;
    if (!cityName) {
      setSelectedCity(null);
      return;
    }
    const city = cityList.find((c) => c.cityName === cityName);
    if (city) {
      setSelectedCity({
        name: city.cityName,
        state: city.state,
        lat: city.lat,
        lng: city.lng,
        defaultRadius: 15,
        path: city.path,
      });
    } else {
      setSelectedCity(null);
    }
  };

  // Shared filter params used by both loadAllData and child components
  const filterParams = useMemo(() => {
    const params = {
      startDate: filters.startDate,
      endDate: filters.endDate,
    };
    if (filters.eventId) params.eventId = filters.eventId;
    if (filters.eventSlug) params.eventSlug = filters.eventSlug;
    if (selectedCity) {
      params.cityLat = selectedCity.lat;
      params.cityLng = selectedCity.lng;
      params.cityRadius = selectedCity.defaultRadius;
      params.cityPath = selectedCity.path;
    }
    if (keywordFilter) {
      params.keyword = keywordFilter;
    }
    return params;
  }, [filters, selectedCity, keywordFilter]);

  const loadAllData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Separate params for funnel (includes restaurantId)
      const funnelParams = { ...filterParams };
      if (filters.restaurantId)
        funnelParams.restaurantId = filters.restaurantId;

      const searchVolumeParams = { ...filterParams, excludePans: true };

      const [
        heatmap,
        searchVolume,
        keywords,
        trafficSources,
        restaurants,
        uniqueVisitors,
        totalUniqueVisitorsResult,
        eventPerformance,
        engagementFunnel,
      ] = await Promise.all([
        getHeatmapData(filterParams),
        getSearchVolume(searchVolumeParams),
        getPopularKeywords(filterParams),
        getTrafficSources(filterParams),
        getMostClickedRestaurants(filterParams),
        getUniqueVisitors(filterParams),
        getTotalUniqueVisitors(filterParams),
        getEventPerformance(filterParams),
        getEngagementFunnel(funnelParams),
      ]);

      setData({
        heatmap,
        searchVolume,
        keywords,
        trafficSources,
        restaurants,
        uniqueVisitors,
        eventPerformance,
        engagementFunnel,
      });

      // Calculate summary
      const totalSearches = searchVolume.reduce(
        (sum, item) => sum + item.count,
        0
      );
      const totalClicks = restaurants.reduce(
        (sum, item) => sum + item.count,
        0
      );

      // Compute website/directions totals from funnel data
      let totalWebsiteClicks = 0;
      let totalDirectionsClicks = 0;
      if (Array.isArray(engagementFunnel)) {
        engagementFunnel.forEach((f) => {
          totalWebsiteClicks += f.restaurantWebsiteClicks || 0;
          totalDirectionsClicks += f.restaurantDirectionsClicks || 0;
        });
      }

      setSummary({
        totalSearches,
        totalClicks,
        uniqueVisitors: totalUniqueVisitorsResult.total || 0,
        activeEvents: eventPerformance.length,
        totalWebsiteClicks,
        totalDirectionsClicks,
      });
    } catch (err) {
      console.error("Error loading analytics data:", err);
      setError("Failed to load analytics data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (activeTab === "site" && loading && Object.keys(data.heatmap).length === 0) {
    return (
      <DashboardContainer>
        <TabBar>
          <TabButton active={activeTab === "site"} onClick={() => setActiveTab("site")}>
            Site Analytics
          </TabButton>
          <TabButton active={activeTab === "adhoc"} onClick={() => setActiveTab("adhoc")}>
            Discovery Search
          </TabButton>
        </TabBar>
        <LoadingMessage>Loading analytics data...</LoadingMessage>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      {error && <ErrorMessage>{error}</ErrorMessage>}

      <TabBar>
        <TabButton active={activeTab === "site"} onClick={() => setActiveTab("site")}>
          Site Analytics
        </TabButton>
        <TabButton active={activeTab === "adhoc"} onClick={() => setActiveTab("adhoc")}>
          Discovery Search
        </TabButton>
      </TabBar>

      <PresetBar>
        {["7d", "30d", "90d", "1y", "custom"].map((preset) => (
          <PresetButton
            key={preset}
            active={timePreset === preset}
            onClick={() => applyTimePreset(preset)}
          >
            {preset === "custom" ? "Custom" : preset}
          </PresetButton>
        ))}
      </PresetBar>

      {(activeTab === "site" || showCustomDates || timePreset === "custom") && (
      <FilterBar>
        {(showCustomDates || timePreset === "custom") && (
          <>
            <div>
              <FilterLabel>Start Date:</FilterLabel>
              <FilterInput
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange("startDate", e.target.value)}
              />
            </div>
            <div>
              <FilterLabel>End Date:</FilterLabel>
              <FilterInput
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange("endDate", e.target.value)}
              />
            </div>
          </>
        )}
        {activeTab === "site" && (
          <>
            <div>
              <FilterLabel>City:</FilterLabel>
              <CitySelect
                value={selectedCity?.name || ""}
                onChange={handleCityChange}
              >
                <option value="">All Cities</option>
                {cityList.map((city) => (
                  <option key={city.path} value={city.cityName}>
                    {city.cityName}, {city.state}
                  </option>
                ))}
              </CitySelect>
            </div>
            <div>
              <FilterLabel>Event Slug:</FilterLabel>
              <FilterInput
                type="text"
                placeholder="Optional"
                value={filters.eventSlug}
                onChange={(e) => {
                  handleFilterChange("eventSlug", e.target.value);
                  handleFilterChange("eventId", "");
                }}
              />
            </div>
            <div>
              <FilterLabel>Event ID:</FilterLabel>
              <FilterInput
                type="text"
                placeholder="Optional"
                value={filters.eventId}
                onChange={(e) => {
                  handleFilterChange("eventId", e.target.value);
                  handleFilterChange("eventSlug", "");
                }}
              />
            </div>
          </>
        )}
      </FilterBar>
      )}

      {activeTab === "site" && (selectedCity || keywordFilter) && (
        <div style={{ marginBottom: "15px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {selectedCity && (
            <FilterChip>
              City: {selectedCity.name}, {selectedCity.state}
              <button onClick={() => setSelectedCity(null)}>&times;</button>
            </FilterChip>
          )}
          {keywordFilter && (
            <FilterChip>
              Keyword: "{keywordFilter}"
              <button onClick={() => setKeywordFilter(null)}>&times;</button>
            </FilterChip>
          )}
        </div>
      )}

      {activeTab === "adhoc" && (
        <AdHocAnalyticsSection
          filters={{ startDate: filters.startDate, endDate: filters.endDate }}
        />
      )}

      {activeTab === "site" && <>
      <MetricsCards
        cards={[
          { title: "Total Searches", value: summary.totalSearches.toLocaleString() },
          { title: "Restaurant Clicks", value: summary.totalClicks.toLocaleString() },
          { title: "Website Clicks", value: (summary.totalWebsiteClicks || 0).toLocaleString() },
          { title: "Directions Clicks", value: (summary.totalDirectionsClicks || 0).toLocaleString() },
          { title: "Unique Visitors", value: summary.uniqueVisitors.toLocaleString() },
          { title: "Active Events", value: summary.activeEvents },
        ]}
      />

      <UniqueVisitorsTrend filters={filterParams} />

      <Section>
        <SectionTitle>Geographic Heatmap - Search Query Locations</SectionTitle>
        <GeographicHeatmap
          data={data.heatmap}
          loading={loading}
          selectedCity={selectedCity}
          onCitySelect={(city) => setSelectedCity(city)}
        />
      </Section>

      <Section>
        <SectionTitle>
          Search Volume Over Time
          {data.searchVolume.length > 0 && (
            <ExportButton onClick={() => exportToCsv(data.searchVolume, "search-volume")}>
              Export CSV
            </ExportButton>
          )}
        </SectionTitle>
        <SearchVolumeChart data={data.searchVolume} loading={loading} />
      </Section>

      <TwoColumnGrid>
        <Section>
          <SectionTitle>
            Popular Keywords
            {data.keywords.length > 0 && (
              <ExportButton onClick={() => exportToCsv(data.keywords, "popular-keywords")}>
                Export CSV
              </ExportButton>
            )}
          </SectionTitle>
          <PopularKeywordsChart
            data={data.keywords}
            loading={loading}
            onKeywordSelect={(kw) => setKeywordFilter(kw)}
          />
        </Section>

        <Section>
          <SectionTitle>
            Most Clicked Restaurants
            {data.restaurants.length > 0 && (
              <ExportButton onClick={() => exportToCsv(data.restaurants, "restaurant-clicks")}>
                Export CSV
              </ExportButton>
            )}
          </SectionTitle>
          <MostClickedRestaurants data={data.restaurants} loading={loading} />
        </Section>
      </TwoColumnGrid>

      <Section>
        <SectionTitle>
          Traffic Sources
          {data.trafficSources.length > 0 && (
            <ExportButton onClick={() => exportToCsv(data.trafficSources, "traffic-sources")}>
              Export CSV
            </ExportButton>
          )}
        </SectionTitle>
        <TrafficSourcesChart data={data.trafficSources} loading={loading} />
      </Section>

      {selectedCity && cityData && (
        <TwoColumnGrid>
          <Section>
            <SectionTitle>
              Zero-Result Queries ({selectedCity.name})
              {cityData.zeroResultQueries.length > 0 && (
                <ExportButton onClick={() => exportToCsv(cityData.zeroResultQueries, "zero-result-queries")}>
                  Export CSV
                </ExportButton>
              )}
            </SectionTitle>
            <ZeroResultQueries data={cityData.zeroResultQueries} loading={!cityData} />
          </Section>

          <Section>
            <SectionTitle>
              Cuisine Demand ({selectedCity.name})
              {cityData.cuisineDemand.length > 0 && (
                <ExportButton onClick={() => exportToCsv(cityData.cuisineDemand, "cuisine-demand")}>
                  Export CSV
                </ExportButton>
              )}
            </SectionTitle>
            <CuisineDemandChart data={cityData.cuisineDemand} loading={!cityData} />
          </Section>
        </TwoColumnGrid>
      )}

      <Section>
        <SectionTitle>Event Performance</SectionTitle>
        <EventPerformance data={data.eventPerformance} loading={loading} />
      </Section>

      <Section>
        <SectionTitle>Restaurant Engagement Funnel</SectionTitle>
        <EngagementFunnel
          data={data.engagementFunnel}
          loading={loading}
          currentRestaurantId={filters.restaurantId}
          onRestaurantFilterChange={(restaurantId) => {
            setFilters((prev) => ({
              ...prev,
              restaurantId: restaurantId,
            }));
          }}
        />
      </Section>

      <SectionTitle style={{ marginTop: "40px", marginBottom: "20px" }}>
        User Journeys
      </SectionTitle>

      <Section>
        <SectionTitle>Landing Page Distribution</SectionTitle>
        <LandingPagePieChart filters={filterParams} />
      </Section>

      <TwoColumnGrid>
        <Section>
          <SectionTitle>Funnel by Landing Page</SectionTitle>
          <LandingPageFunnel filters={filterParams} />
        </Section>

        <Section>
          <SectionTitle>Action Transitions</SectionTitle>
          <SessionFlowTable filters={filterParams} />
        </Section>
      </TwoColumnGrid>
      </>}
    </DashboardContainer>
  );
};

const mapStateToProps = (state) => ({
  token: state.auth.token,
  groups: state.auth.user?.groups || [],
});

export default connect(mapStateToProps)(AnalyticsDashboard);
